import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';

// ─────────────────────────────────────────────────────────────
// « Recherches IA par offre »
// Récapitulatif des recherches effectuées par les clients dans
// l'assistant IA : quelle offre a été cherchée, par qui, et combien
// de fois. Deux lectures : par offre et par client.
// ─────────────────────────────────────────────────────────────

type Period = '7' | '30' | '90' | 'custom';
type View = 'offers' | 'clients';

interface EventRow {
  id: number;
  client_user_id: number | null;
  client_name: string | null;
  event_type: string | null;
  product_id: number | null;
  product_name: string | null;
  query: string | null;
  created_at: string;
}

interface OfferStat {
  key: string;
  label: string;
  searches: number;
  clients: string[];
  carts: number;
  lastAt: string;
}

interface ClientStat {
  key: string;
  label: string;
  searches: number;
  offers: string[];
  lastAt: string;
}

const PERIODS: { value: Period; label: string }[] = [
  { value: '7', label: '7 j' },
  { value: '30', label: '30 j' },
  { value: '90', label: '90 j' },
  { value: 'custom', label: 'Personnalisé' },
];

/** Bornes ISO de la période analysée (7/30/90 jours ou plage personnalisée). */
function rangeIso(period: Period, from: string, to: string): { start: string; end: string } {
  if (period === 'custom' && from) {
    const start = new Date(`${from}T00:00:00`);
    const end = new Date(`${to || from}T23:59:59`);
    return {
      start: (Number.isNaN(start.getTime()) ? new Date() : start).toISOString(),
      end: (Number.isNaN(end.getTime()) ? new Date() : end).toISOString(),
    };
  }
  const d = new Date();
  d.setDate(d.getDate() - parseInt(period, 10));
  return { start: d.toISOString(), end: new Date().toISOString() };
}

/** Échappe une valeur pour un CSV lisible dans Excel (séparateur ;). */
function csvCell(value: string | number): string {
  const text = String(value ?? '').replace(/"/g, '""');
  return `"${text}"`;
}

function timeAgo(iso: string): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  return `il y a ${d} j`;
}

function clean(text: string | null | undefined): string {
  return String(text || '').trim();
}

export default function AiOfferSearches() {
  const { user } = useAuth();
  const [period, setPeriod] = useState<Period>('30');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [view, setView] = useState<View>('offers');
  const [filter, setFilter] = useState('');
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const merchantId = getCommerceId(user);

  const load = useCallback(async (p: Period, from: string, to: string) => {
    if (!merchantId) return;
    setLoading(true);
    setError(null);
    try {
      const range = rangeIso(p, from, to);
      const { data, error: err } = await supabase
        .from('ai_chat_product_events')
        .select('id, client_user_id, client_name, event_type, product_id, product_name, query, created_at')
        .eq('merchant_id', merchantId)
        .gte('created_at', range.start)
        .lte('created_at', range.end)
        .order('created_at', { ascending: false })
        .limit(1500);
      if (err) throw err;
      setEvents((data || []) as EventRow[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les recherches IA');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => { void load(period, fromDate, toDate); }, [load, period, fromDate, toDate]);

  // ── Par offre ──
  const offers = useMemo<OfferStat[]>(() => {
    const map = new Map<string, OfferStat>();
    events.forEach((e) => {
      const offer = clean(e.product_name) || clean(e.query);
      if (!offer) return;
      const key = offer.toLowerCase();
      const current = map.get(key) || {
        key,
        label: offer,
        searches: 0,
        clients: [],
        carts: 0,
        lastAt: e.created_at,
      };
      current.searches += 1;
      if (e.event_type === 'cart') current.carts += 1;
      const client = clean(e.client_name) || (e.client_user_id ? `Client #${e.client_user_id}` : '');
      if (client && !current.clients.includes(client)) current.clients.push(client);
      if (!current.lastAt || new Date(e.created_at).getTime() > new Date(current.lastAt).getTime()) {
        current.lastAt = e.created_at;
      }
      map.set(key, current);
    });
    return [...map.values()].sort((a, b) => b.searches - a.searches);
  }, [events]);

  // ── Par client ──
  const clients = useMemo<ClientStat[]>(() => {
    const map = new Map<string, ClientStat>();
    events.forEach((e) => {
      const clientName = clean(e.client_name) || (e.client_user_id ? `Client #${e.client_user_id}` : '');
      if (!clientName) return;
      const key = String(e.client_user_id || clientName.toLowerCase());
      const current = map.get(key) || { key, label: clientName, searches: 0, offers: [], lastAt: e.created_at };
      current.searches += 1;
      const offer = clean(e.product_name) || clean(e.query);
      if (offer && !current.offers.includes(offer)) current.offers.push(offer);
      if (!current.lastAt || new Date(e.created_at).getTime() > new Date(current.lastAt).getTime()) {
        current.lastAt = e.created_at;
      }
      map.set(key, current);
    });
    return [...map.values()].sort((a, b) => b.searches - a.searches);
  }, [events]);

  const needle = filter.trim().toLowerCase();
  const filteredOffers = needle
    ? offers.filter((o) => o.label.toLowerCase().includes(needle) || o.clients.some((c) => c.toLowerCase().includes(needle)))
    : offers;
  const filteredClients = needle
    ? clients.filter((c) => c.label.toLowerCase().includes(needle) || c.offers.some((o) => o.toLowerCase().includes(needle)))
    : clients;

  /** Export CSV de la lecture affichée (offres ou clients). */
  const exportCsv = () => {
    const range = rangeIso(period, fromDate, toDate);
    const periodLabel = period === 'custom'
      ? `${fromDate || '—'} → ${toDate || fromDate || '—'}`
      : `${range.start.slice(0, 10)} → ${range.end.slice(0, 10)}`;

    const lines: string[] = [];
    if (view === 'offers') {
      lines.push(['Offre recherchée', 'Recherches', 'Clients uniques', 'Ajouts au panier', 'Dernière recherche'].map(csvCell).join(';'));
      filteredOffers.forEach((o) => {
        lines.push([o.label, o.searches, o.clients.length, o.carts, new Date(o.lastAt).toLocaleString('fr-FR')].map(csvCell).join(';'));
      });
    } else {
      lines.push(['Client', 'Recherches', 'Offres consultées', 'Dernière activité'].map(csvCell).join(';'));
      filteredClients.forEach((c) => {
        lines.push([c.label, c.searches, c.offers.join(' | '), new Date(c.lastAt).toLocaleString('fr-FR')].map(csvCell).join(';'));
      });
    }
    if (lines.length === 1) return;

    const meta = [
      `# Recherches IA — ${view === 'offers' ? 'par offre' : 'par client'}`,
      `# Période: ${periodLabel}`,
      `# Généré le ${new Date().toLocaleString('fr-FR')}`,
    ];
    const csv = `\uFEFF${[...meta, ...lines].join('\r\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recherches-ia-${view}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const totalSearches = events.length;
  const uniqueOffers = offers.length;
  const uniqueClients = clients.length;
  const maxSearches = offers[0]?.searches || 1;

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-search-eye-line text-lg"></i>
          </span>
          <div>
            <h3 className="text-sm font-semibold text-foreground-900">Recherches IA par offre</h3>
            <p className="text-[11px] text-foreground-500">Qui a cherché quoi, et combien de fois — depuis le chat IA</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-1">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  period === p.value ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {period === 'custom' && (
            <div className="flex flex-wrap items-center justify-end gap-1.5">
              <input
                type="date"
                value={fromDate}
                max={toDate || undefined}
                onChange={(e) => setFromDate(e.target.value)}
                aria-label="Date de début"
                className="px-2.5 py-1.5 rounded-full border border-background-200/70 bg-background-50 text-xs text-foreground-700 cursor-pointer focus:outline-none focus:border-primary-300"
              />
              <span className="text-foreground-400 text-xs">→</span>
              <input
                type="date"
                value={toDate}
                min={fromDate || undefined}
                onChange={(e) => setToDate(e.target.value)}
                aria-label="Date de fin"
                className="px-2.5 py-1.5 rounded-full border border-background-200/70 bg-background-50 text-xs text-foreground-700 cursor-pointer focus:outline-none focus:border-primary-300"
              />
            </div>
          )}
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="rounded-lg bg-secondary-50 border border-secondary-100 p-3">
          <div className="text-xl font-bold text-foreground-950 tabular-nums">{totalSearches.toLocaleString('fr-FR')}</div>
          <div className="text-[11px] text-foreground-500 mt-0.5">Recherches</div>
        </div>
        <div className="rounded-lg bg-accent-50 border border-accent-100 p-3">
          <div className="text-xl font-bold text-foreground-950 tabular-nums">{uniqueOffers.toLocaleString('fr-FR')}</div>
          <div className="text-[11px] text-foreground-500 mt-0.5">Offres recherchées</div>
        </div>
        <div className="rounded-lg bg-primary-50 border border-primary-100 p-3">
          <div className="text-xl font-bold text-foreground-950 tabular-nums">{uniqueClients.toLocaleString('fr-FR')}</div>
          <div className="text-[11px] text-foreground-500 mt-0.5">Clients uniques</div>
        </div>
        <div className="rounded-lg bg-background-100 border border-background-200/70 p-3">
          <div className="text-xl font-bold text-foreground-950 tabular-nums">
            {offers.reduce((s, o) => s + o.carts, 0).toLocaleString('fr-FR')}
          </div>
          <div className="text-[11px] text-foreground-500 mt-0.5">Ajouts au panier</div>
        </div>
      </div>

      {/* Contrôles */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
          <button
            onClick={() => setView('offers')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
              view === 'offers' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            Par offre
          </button>
          <button
            onClick={() => setView('clients')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
              view === 'clients' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            Par client
          </button>
        </div>
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={view === 'offers' ? 'Filtrer une offre ou un client…' : 'Filtrer un client ou une offre…'}
            className="w-full pl-8 pr-3 py-2 rounded-full border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
          />
        </div>
        {filter && (
          <button
            onClick={() => setFilter('')}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-close-line"></i>Effacer
          </button>
        )}
        <button
          onClick={exportCsv}
          disabled={loading || (view === 'offers' ? filteredOffers.length : filteredClients.length) === 0}
          title="Exporter la lecture affichée en CSV (Excel)"
          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-accent-50 text-accent-700 text-xs font-medium hover:bg-accent-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap ml-auto"
        >
          <i className="ri-file-excel-2-line"></i>
          Export CSV
        </button>
      </div>

      {error && (
        <div className="flex flex-col items-center justify-center py-6">
          <p className="text-sm text-red-600 mb-3">{error}</p>
          <button
            onClick={() => void load(period, fromDate, toDate)}
            className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Réessayer
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : view === 'offers' ? (
        filteredOffers.length === 0 ? (
          <EmptyState text="Aucune recherche IA sur cette période." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[560px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-foreground-400 border-b border-background-200/70">
                  <th className="py-2 pr-3 font-semibold">Offre recherchée</th>
                  <th className="py-2 px-3 font-semibold text-right">Recherches</th>
                  <th className="py-2 px-3 font-semibold text-right">Clients</th>
                  <th className="py-2 px-3 font-semibold text-right">Panier</th>
                  <th className="py-2 pl-3 font-semibold text-right">Dernière</th>
                </tr>
              </thead>
              <tbody>
                {filteredOffers.map((o) => (
                  <tr key={o.key} className="border-b border-background-200/50 last:border-0 align-top">
                    <td className="py-2.5 pr-3">
                      <div className="text-sm text-foreground-900 truncate max-w-[260px]" title={o.label}>{o.label}</div>
                      <div className="mt-1 h-1.5 bg-background-200/70 rounded-full overflow-hidden w-full max-w-[220px]">
                        <div
                          className="h-full bg-secondary-400 rounded-full transition-all"
                          style={{ width: `${Math.max(6, (o.searches / maxSearches) * 100)}%` }}
                        ></div>
                      </div>
                      {o.clients.length > 0 && (
                        <div className="text-[10px] text-foreground-400 mt-1 truncate max-w-[260px]">
                          {o.clients.slice(0, 3).join(', ')}{o.clients.length > 3 ? ` +${o.clients.length - 3}` : ''}
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right text-sm font-semibold text-foreground-900 tabular-nums">{o.searches}</td>
                    <td className="py-2.5 px-3 text-right text-sm text-foreground-700 tabular-nums">{o.clients.length}</td>
                    <td className="py-2.5 px-3 text-right">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        o.carts > 0 ? 'bg-primary-100 text-primary-700' : 'bg-background-100 text-foreground-400'
                      }`}>
                        <i className="ri-shopping-cart-2-line text-[10px]"></i>{o.carts}
                      </span>
                    </td>
                    <td className="py-2.5 pl-3 text-right text-[11px] text-foreground-400 whitespace-nowrap">{timeAgo(o.lastAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : filteredClients.length === 0 ? (
        <EmptyState text="Aucun client n'a encore utilisé l'assistant IA sur cette période." />
      ) : (
        <div className="space-y-2">
          {filteredClients.map((c) => (
            <div key={c.key} className="flex items-start gap-3 py-2.5 border-b border-background-200/50 last:border-0">
              <span className="w-8 h-8 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-user-search-line text-sm"></i>
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-foreground-900 truncate">{c.label}</div>
                <div className="text-[11px] text-foreground-500 truncate">
                  {c.offers.slice(0, 4).join(' · ')}{c.offers.length > 4 ? ` +${c.offers.length - 4}` : ''}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-sm font-semibold text-foreground-900 tabular-nums">{c.searches}</div>
                <div className="text-[10px] text-foreground-400">recherche{c.searches > 1 ? 's' : ''}</div>
              </div>
              <span className="text-[10px] text-foreground-400 whitespace-nowrap mt-0.5">{timeAgo(c.lastAt)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
      <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mb-2">
        <i className="ri-search-eye-line text-xl text-foreground-400"></i>
      </div>
      <p className="text-sm text-foreground-500">{text}</p>
      <p className="text-[11px] text-foreground-400 mt-1">Les recherches de vos clients dans l&apos;assistant apparaîtront ici.</p>
    </div>
  );
}